<script>
import { mapGetters } from 'vuex';

import Layout from '@layouts/main.vue';
import NoticeList from '@components/current-news/notice-list.vue';
import ArticlesGrid from '@components/current-news/articles-grid.vue';

import Translatable from '@src/mixins/translatable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';
import ImageMixin from '@src/mixins/imageMixin.js';
import Resizable from '@src/mixins/resizable.js';
import AuthorLabel from '@src/mixins/author.js';
import { sharedBlocksMixin } from '@src/mixins/sharedBlocksMixin.js';
import SeoMixin from '@src/mixins/seo.js';

import SharedBlockSlot from '@components/sharedBlocks/SharedBlockSlot.vue';

export default {
  // page: {
  //   title: 'Current News',
  //   meta: [{ name: 'description', content: 'Current News Page.' }],
  // },
  components: {
    Layout,
    NoticeList,
    ArticlesGrid,
    SharedBlockSlot,
  },
  mixins: [
    Translatable,
    SanitizerMixin,
    ImageMixin,
    Resizable,
    AuthorLabel,
    sharedBlocksMixin,
    SeoMixin,
  ],
  computed: {
    ...mapGetters('currentNews', ['pageData']),
    seoEntity() {
      return {
        ...this.pageData,
        isStatic: true,
      };
    },
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen ? this.pageData.mainImage.web : this.pageData.mainImage.app;
      }
      return null;
    },
    isWhatsOn() {
      return (
        this.$route.meta.tab === 'whats-on' ||
        this.$route.name === `news_whatson__${this.currentLanguage}`
      );
    },
    isNotice() {
      return !this.isWhatsOn;
    },
  },
  watch: {
    pageData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.slugs) {
          this.$store.commit('translations/SET_ALTERNATE_SLUGS', newVal.slugs);
        }
      },
    },
  },
  beforeCreate() {
    // this.$store.dispatch('attractions/fetchAttractions')
  },
  provide() {
    const provided = {};
    Object.defineProperty(provided, 'sortedSharedBlocks', {
      enumerable: true,
      get: () => this.sortedSharedBlocks,
    });
    return {
      sharedBlocksProvider: provided,
    };
  },
  methods: {
    changeType(type) {
      if (type === 'whats-on') {
        this.$router.push({ name: `news_whatson__${this.currentLanguage}` }).catch(() => {});
      } else {
        this.$router.push({ name: `news__${this.currentLanguage}` }).catch(() => {});
      }
    },
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
  },
};
</script>

<template>
  <Layout>
    <section
      v-if="pageData && pageData.content && pageImg"
      class="header justify-center"
      :style="getBackground(pageImg.url)"
      :title="getSeoText(pageImg.title)"
      role="img"
      :aria-label="getSeoText(pageImg.alt)"
    >
      <div class="header-block">
        <h1 class="header-title" v-html="getTranslatedContent('title', pageData.content)"></h1>
        <h3
          class="header-subtitle"
          v-html="getTranslatedContent('subtitle', pageData.content)"
        ></h3>
      </div>
    </section>
    <section class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div v-html="bannerPageText(pageData.content)"></div>
      </div>
    </section>
    <section class="justify-center">
      <div class="buttons-container">
        <button
          :class="{ active: isNotice }"
          class="button button-left"
          @click="changeType('notice')"
        >
          {{ getTranslatedContent('NEWS_closures_and_notices_tab') }}
        </button>
        <button
          :class="{ active: isWhatsOn }"
          class="button button-right"
          @click="changeType('whats-on')"
        >
          {{ getTranslatedContent('NEWS_whats_on_tab') }}
        </button>
      </div>
    </section>
    <section class="justify-center">
      <div class="main-container">
        <ArticlesGrid v-if="isWhatsOn" />
        <NoticeList v-if="isNotice" />
      </div>
    </section>
    <SharedBlockSlot :slot-index="1" />
    <SharedBlockSlot :slot-index="2" />
    <SharedBlockSlot :slot-index="3" />
    <SharedBlockSlot :slot-index="4" />
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.link {
  text-decoration: none;
  height: 100%;
  width: 100%;
}

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

::v-deep .description-banner {
  margin: -99px 0 32px;
  position: relative;
  & .description {
    width: 1140px;
    min-height: 100px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    padding: 25px 68px;
    user-select: none;

    p {
      font-family: Open Sans, sans-serif;
      font-weight: normal;
      font-style: normal;
      font-size: 16px;
      line-height: 21px;
      color: $dark-blue;
    }

    & p:empty {
      height: 15px;
      margin: 0;
    }

    & p:not(:last-child):not(:empty) {
      margin-bottom: 15px;
    }

    & p > br {
      display: none;
    }
    & p:has(br:last-child)::after {
      content: '';
      display: block;
      height: 15px;
    }

    & em {
      font-style: italic;
    }

    & a {
      color: $gray;
      text-decoration: none;

      &:hover {
        color: $orange;
      }
    }

    & ul {
      margin-left: 20px;
      list-style: initial;
    }

    & strong {
      font-family: Open Sans Bold, sans-serif;
      font-style: normal;
      font-weight: bold;
    }

    & .ql-align-center {
      text-align: center;
    }
  }
}

.buttons-container {
  width: 1141px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  border: 1px solid $dark-blue;
  height: 80px;
  padding: 2px;
}

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: $white;
  border: 1px solid $white;
  color: $dark-blue;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-style: normal;
  text-transform: uppercase;
  font-size: 22px;
  line-height: 29px;
  cursor: pointer;
  user-select: none;

  &.active,
  &:hover {
    background: $dark-blue;
    color: $white;
  }

  &.button-left {
    border-radius: 8px 0px 0px 8px;
  }

  &.button-right {
    border-radius: 0px 8px 8px 0px;
  }
}

@media only screen and (max-width: $screen-width-xs) {
  .main-container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 10px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 40px;

    & .description {
      flex-direction: column;
      width: 100%;
      min-height: 240px;
      padding: 20px 8px;

      .text-container {
        width: 100%;
      }

      p {
        font-size: 14px;
        line-height: 23px;
      }
    }
  }

  .buttons-container {
    width: 100%;
    height: 51px;
    border-radius: 4px;
    padding: 0px;
    margin: 0 5px;
  }

  .button {
    font-size: 12px;
    line-height: 16px;

    &.button-left {
      border-radius: 3px 0px 0px 3px;
    }

    &.button-right {
      border-radius: 0px 3px 3px 0px;
    }
  }
}
</style>
